import { Recnum, UUID } from '../../types/common';
import { DTO } from '../../utilities/Dto';
import { MakeOptionalExcept } from '../../utilities/MakeOptionalExcept';
import { assertNever } from '../../utilities/assertNever';
import { v4 as uuidV4 } from 'uuid';

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

// FIXME: Naming inconsistency across domains.
// In the Tickets domain, "attachment" specifically means a file upload,
// "message" is a text note, and "location" is a geotag.
// In the Checklists domain, "Attachment" is a union of all three sub-types:
//   - FileAttachment  (maps to Ticket's "attachment")
//   - NoteAttachment  (maps to Ticket's "message")
//   - GeotagAttachment (maps to Ticket's "location")
// We keep the current names because they match the legacy code and database
// payloads. A future unification should align both domains.

interface BaseAttachment {
  id: UUID;
  type: string;
  user_id: number;
  created: Date;
}

/**
 * Simplified position type compatible with the browser GeolocationPosition
 * but usable in non-browser contexts (Node, tests).
 */
export interface ListPosition {
  coords: {
    speed: number | null;
    heading: number | null;
    accuracy: number | null;
    altitude: number | null;
    altitudeAccuracy: number | null;
    latitude: number;
    longitude: number;
  };
  timestamp: number;
}

export interface Geotag {
  label?: string | null;
  position: ListPosition;
}

export interface FileAttachment extends BaseAttachment {
  type: 'attachment';
  checksum: string;
  contentType: string;
  size: number;
  filename: string | null;
}

export interface GeotagAttachment extends BaseAttachment {
  type: 'geotag';
  geotag: Geotag;
}

export interface NoteAttachment extends BaseAttachment {
  type: 'note';
  text: string;
}

export type Attachment = FileAttachment | GeotagAttachment | NoteAttachment;

// ---------------------------------------------------------------------------
// Proto reference
// ---------------------------------------------------------------------------

export interface ProtoReference {
  listId: UUID;
  nodeId: UUID;
}

// ---------------------------------------------------------------------------
// Field hierarchy
// ---------------------------------------------------------------------------

export interface BaseField {
  id: UUID;
  created: Date;
  type: string;
  parentNodeId: UUID | null;
  protoChain: ProtoReference[];
  modifiedFields: string[];
  /**
   * id of the event that brought this node into existence —
   * INSERT, COPY, or EXPAND_PROTO. Stamped by the reducer.
   * Unset for the root field and for nodes built without an
   * originating event (legacy data).
   */
  originEventId?: UUID;
}

export interface StructureField extends BaseField {
  children: Field[];
}

export interface RootField extends StructureField {
  type: 'ROOT_FIELD';
}

export interface GroupField extends StructureField {
  type: 'GROUP_FIELD';
  title_en: string;
  title_nl: string;
}

export interface ValueField extends BaseField {
  value: boolean | number | null;
  title_en: string;
  title_nl: string;
  description_en: string;
  description_nl: string;
  attachments: Attachment[];
}

export interface CheckpointField extends ValueField {
  type: 'CHECKPOINT_FIELD';
  value: boolean | null;
}

export interface NumericField extends ValueField {
  type: 'NUMERIC_FIELD';
  value: number | null;
  unit?: string;
}

export interface TaskField extends ValueField {
  type: 'TASK_FIELD';
  value: boolean | null;
}

export type Field =
  | RootField
  | GroupField
  | CheckpointField
  | NumericField
  | TaskField;

// ---------------------------------------------------------------------------
// Partial field types (for INSERT payloads)
// ---------------------------------------------------------------------------

/** Fields that are set automatically, not supplied in partials */
type AutoFields =
  | 'parentNodeId'
  | 'modifiedFields'
  | 'protoChain'
  | 'originEventId';

export type PartialGroupField = Omit<
  Partial<GroupField> & Pick<GroupField, 'id' | 'created' | 'type'>,
  AutoFields | 'children'
>;

export type PartialCheckpointField = Omit<
  Partial<CheckpointField> & Pick<CheckpointField, 'id' | 'created' | 'type'>,
  AutoFields
>;

export type PartialNumericField = Omit<
  Partial<NumericField> & Pick<NumericField, 'id' | 'created' | 'type'>,
  AutoFields
>;

export type PartialTaskField = Omit<
  Partial<TaskField> & Pick<TaskField, 'id' | 'created' | 'type'>,
  AutoFields
>;

export type PartialField =
  | PartialGroupField
  | PartialCheckpointField
  | PartialNumericField
  | PartialTaskField;

/** For PATCH payloads: any patchable field property */
export type PatchField = {
  type?: 'CHECKPOINT_FIELD' | 'NUMERIC_FIELD' | 'TASK_FIELD' | 'GROUP_FIELD';
  title_en?: string;
  title_nl?: string;
  description_en?: string;
  description_nl?: string;
  value?: boolean | number | null;
  children?: Field[];
};

// ---------------------------------------------------------------------------
// List event types (18 total - must match database enum exactly)
// ---------------------------------------------------------------------------

export type ListEventType =
  | 'CREATE_LIST'
  | 'MODIFY_LIST'
  | 'INSERT'
  | 'EXPAND_PROTO'
  | 'PATCH'
  | 'REMOVE'
  | 'MOVE'
  | 'COPY'
  | 'CLOSE_LIST'
  | 'OPEN_LIST'
  | 'ADD_ATTACHMENT'
  | 'REMOVE_ATTACHEMNT' // intentional typo - matches database enum
  | 'ADD_NOTE'
  | 'EDIT_NOTE'
  | 'REMOVE_NOTE'
  | 'ADD_GEOTAG'
  | 'REMOVE_GEOTAG'
  | 'EDIT_GEOTAG';

// ---------------------------------------------------------------------------
// Event payloads
// ---------------------------------------------------------------------------

export interface CreateListPayload {
  name?: string;
  werk_id?: number | null;
  associated_users?: number[];
  tags?: string[];
  start_date?: Date;
  end_date?: Date;
}

export interface ModifyListPayload {
  name?: string;
  werk_id?: number | null;
  associated_users?: number[];
  tags?: string[];
  start_date?: Date;
  end_date?: Date;
}

export interface InsertPayload {
  node: PartialField;
  targetNodeId: UUID;
  index?: number;
}

export interface PatchPayload {
  partialNode: PatchField;
  targetNodeId: UUID;
}

export interface RemovePayload {
  targetNodeId: UUID;
}

export interface MovePayload {
  targetNodeId: UUID;
  subjectNodeId: UUID;
  index?: number;
}

export interface CopyPayload {
  targetNodeId: UUID;
  subjectNodeId: UUID;
  index?: number;
}

export interface ExpandProtoPayload {
  targetNodeId: UUID;
  protoListId: UUID;
}

export interface ListAddAttachmentPayload {
  targetNodeId: UUID;
  attachment: {
    contentType: string;
    checksum: string;
    size: number;
    filename: string | null;
  };
}

export interface RemoveAttachmentPayload {
  targetNodeId: UUID;
  checksum: string;
}

export interface AddNotePayload {
  note: string;
  targetNodeId: UUID;
}

export interface EditNotePayload {
  text: string;
  targetNodeId: UUID;
  noteId: UUID;
}

export interface RemoveNotePayload {
  targetNodeId: UUID;
  noteId: UUID;
}

export interface AddGeotagPayload {
  geotag: Geotag;
  targetNodeId: UUID;
}

export interface EditGeotagPayload {
  geotag: Partial<Geotag>;
  targetNodeId: UUID;
  geotagId: UUID;
}

export interface RemoveGeotagPayload {
  targetNodeId: UUID;
  geotagId: UUID;
}

export type CloseListPayload = Record<string, never>;
export type OpenListPayload = Record<string, never>;

// ---------------------------------------------------------------------------
// Typed events (discriminated union)
// ---------------------------------------------------------------------------

export interface BaseListEvent {
  id: UUID;
  recnum?: Recnum | null;
  type: string;
  payload: unknown;
  list_id: UUID;
  user_id: Recnum;
  created: Date;
  inserted?: Date | null;
}

/**
 * Order in which list events must be reduced: by `created`, ties broken by
 * `recnum` (insertion order). Batches share a `created` timestamp, and the
 * database returns such ties in arbitrary order — a group could then be
 * reduced after the fields inserted into it. Events without a recnum aren't
 * persisted yet and go after persisted events with the same `created`.
 */
export function compareListEvents(
  a: { created: Date | string; recnum?: Recnum | null },
  b: { created: Date | string; recnum?: Recnum | null },
): number {
  const byCreated =
    new Date(a.created).getTime() - new Date(b.created).getTime();
  if (byCreated !== 0) return byCreated;
  return (
    (a.recnum ?? Number.POSITIVE_INFINITY) -
      (b.recnum ?? Number.POSITIVE_INFINITY) || 0
  );
}

type MakeListEvent<Type extends ListEventType, Payload> = {
  type: Type;
  payload: Payload;
} & BaseListEvent;

export type CreateListEvent = MakeListEvent<'CREATE_LIST', CreateListPayload>;
export type ModifyListEvent = MakeListEvent<'MODIFY_LIST', ModifyListPayload>;
export type InsertEvent = MakeListEvent<'INSERT', InsertPayload>;
export type PatchEvent = MakeListEvent<'PATCH', PatchPayload>;
export type RemoveEvent = MakeListEvent<'REMOVE', RemovePayload>;
export type MoveEvent = MakeListEvent<'MOVE', MovePayload>;
export type CopyEvent = MakeListEvent<'COPY', CopyPayload>;
export type ExpandProtoEvent = MakeListEvent<
  'EXPAND_PROTO',
  ExpandProtoPayload
>;
export type AddAttachmentEvent = MakeListEvent<
  'ADD_ATTACHMENT',
  ListAddAttachmentPayload
>;
export type RemoveAttachmentEvent = MakeListEvent<
  'REMOVE_ATTACHEMNT',
  RemoveAttachmentPayload
>;
export type AddNoteEvent = MakeListEvent<'ADD_NOTE', AddNotePayload>;
export type EditNoteEvent = MakeListEvent<'EDIT_NOTE', EditNotePayload>;
export type RemoveNoteEvent = MakeListEvent<'REMOVE_NOTE', RemoveNotePayload>;
export type AddGeotagEvent = MakeListEvent<'ADD_GEOTAG', AddGeotagPayload>;
export type EditGeotagEvent = MakeListEvent<'EDIT_GEOTAG', EditGeotagPayload>;
export type RemoveGeotagEvent = MakeListEvent<
  'REMOVE_GEOTAG',
  RemoveGeotagPayload
>;
export type CloseListEvent = MakeListEvent<'CLOSE_LIST', CloseListPayload>;
export type OpenListEvent = MakeListEvent<'OPEN_LIST', OpenListPayload>;

export type ListEvent =
  | CreateListEvent
  | ModifyListEvent
  | InsertEvent
  | PatchEvent
  | RemoveEvent
  | MoveEvent
  | CopyEvent
  | ExpandProtoEvent
  | AddAttachmentEvent
  | RemoveAttachmentEvent
  | AddNoteEvent
  | EditNoteEvent
  | RemoveNoteEvent
  | AddGeotagEvent
  | EditGeotagEvent
  | RemoveGeotagEvent
  | CloseListEvent
  | OpenListEvent;

// ---------------------------------------------------------------------------
// List status
// ---------------------------------------------------------------------------

export type ListStatus = 'open' | 'closed';

// ---------------------------------------------------------------------------
// List summary (metadata only, no events — used for table views)
// ---------------------------------------------------------------------------

export interface ListSummary {
  id: UUID;
  recnum?: Recnum;
  name: string;
  associated_users: number[];
  tags: string[];
  werk_id: number | null;
  creator_user_id: number | null;
  status: ListStatus;
  close_date: Date | null;
  start_date: Date | null;
  end_date: Date | null;
  created: Date;
  modified: Date;
  deleted: boolean;
  dependencies: UUID[];
  /** Inverse of dependencies: lists that include this list in their dependencies. */
  dependants: UUID[];
  /**
   * Total feedback items attached to this list (as a proto/template).
   * Undefined when the list query did not request feedback aggregates.
   */
  feedback_total_count?: number;
  /**
   * Unresolved (unhandled) feedback items attached to this list (as a proto/template).
   * Undefined when the list query did not request feedback aggregates.
   */
  feedback_unresolved_count?: number;
}

// ---------------------------------------------------------------------------
// List record (with events — used for detail views and building)
// ---------------------------------------------------------------------------

export interface List extends ListSummary {
  events: ListEvent[];
}

// ---------------------------------------------------------------------------
// BuiltList (complete reducer output)
// ---------------------------------------------------------------------------

export interface BuiltList {
  id: UUID;
  recnum?: Recnum;
  name: string;
  associated_users: number[];
  tags: string[];
  werk_id: number | null;
  /** Always set by CREATE_LIST (from event.user_id). Stub value is 0. */
  creator_user_id: number;
  status: ListStatus;
  close_date: Date | null;
  start_date: Date | null;
  end_date: Date | null;
  created: Date;
  modified: Date;
  deleted: boolean;
  dependencies: UUID[];
  dependants: UUID[];
  tree: RootField;
}

// ---------------------------------------------------------------------------
// DTO parsing
// ---------------------------------------------------------------------------

export function parseListSummaryDto(dto: DTO<ListSummary>): ListSummary {
  // PostgREST embedded counts arrive as [{count: N}]. Flatten to numbers when present.
  const rawTotal = (dto as Record<string, unknown>).feedback_total_count;
  const rawUnresolved = (dto as Record<string, unknown>)
    .feedback_unresolved_count;
  return {
    ...dto,
    close_date: dto.close_date ? new Date(dto.close_date) : null,
    start_date: dto.start_date ? new Date(dto.start_date) : null,
    end_date: dto.end_date ? new Date(dto.end_date) : null,
    created: new Date(dto.created),
    modified: new Date(dto.modified),
    dependencies: dto.dependencies ?? [],
    dependants: dto.dependants ?? [],
    feedback_total_count: extractEmbeddedCount(rawTotal),
    feedback_unresolved_count: extractEmbeddedCount(rawUnresolved),
  };
}

/**
 * Extract a count from a PostgREST embedded aggregate.
 * Embedded counts arrive as `[{count: N}]`, but may also already be a number
 * (or undefined when the query did not request the aggregate).
 */
function extractEmbeddedCount(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0] as { count?: unknown };
    if (typeof first?.count === 'number') return first.count;
  }
  return undefined;
}

export function parseListDto(dto: DTO<List>): List {
  return {
    ...parseListSummaryDto(dto),
    events: dto.events.map(parseListEventDto),
  };
}

export function parseListEventDto(event: DTO<ListEvent>): ListEvent {
  const base = {
    created: new Date(event.created),
    inserted:
      typeof event.inserted === 'string'
        ? new Date(event.inserted)
        : event.inserted,
  };

  switch (event.type) {
    case 'CREATE_LIST':
      return {
        ...event,
        ...base,
        payload: {
          ...event.payload,
          start_date:
            typeof event.payload.start_date === 'string'
              ? new Date(event.payload.start_date)
              : event.payload.start_date,
          end_date:
            typeof event.payload.end_date === 'string'
              ? new Date(event.payload.end_date)
              : event.payload.end_date,
        },
      } as CreateListEvent;
    case 'MODIFY_LIST': {
      const payload: Record<string, unknown> = { ...event.payload };
      if ('start_date' in event.payload) {
        payload.start_date =
          typeof event.payload.start_date === 'string'
            ? new Date(event.payload.start_date)
            : event.payload.start_date;
      }
      if ('end_date' in event.payload) {
        payload.end_date =
          typeof event.payload.end_date === 'string'
            ? new Date(event.payload.end_date)
            : event.payload.end_date;
      }
      return { ...event, ...base, payload } as ModifyListEvent;
    }
    case 'INSERT':
      return {
        ...event,
        ...base,
        payload: {
          ...event.payload,
          node: {
            ...event.payload.node,
            created: new Date(event.payload.node.created),
          },
        },
      } as InsertEvent;
    case 'PATCH':
    case 'REMOVE':
    case 'MOVE':
    case 'COPY':
    case 'EXPAND_PROTO':
    case 'CLOSE_LIST':
    case 'OPEN_LIST':
    case 'ADD_ATTACHMENT':
    case 'REMOVE_ATTACHEMNT':
    case 'ADD_NOTE':
    case 'EDIT_NOTE':
    case 'REMOVE_NOTE':
    case 'ADD_GEOTAG':
    case 'EDIT_GEOTAG':
    case 'REMOVE_GEOTAG':
      return { ...event, ...base } as ListEvent;
    default:
      assertNever(event);
  }
}

// ---------------------------------------------------------------------------
// Event factory
// ---------------------------------------------------------------------------

export function createListEvent<
  T extends ListEvent['type'],
  E extends ListEvent = Extract<ListEvent, { type: T }>,
>(
  type: T,
  partial: MakeOptionalExcept<E, 'list_id' | 'payload' | 'user_id'>,
): E {
  return {
    created: new Date(),
    id: uuidV4(),
    inserted: null,
    recnum: null,
    type,
    ...partial,
  } as unknown as E;
}

// ---------------------------------------------------------------------------
// Field constructors
// ---------------------------------------------------------------------------

export function createRootField(
  partial: Partial<RootField> & Pick<RootField, 'id'>,
): RootField {
  return {
    created: new Date(),
    type: 'ROOT_FIELD',
    children: [],
    protoChain: [],
    modifiedFields: [],
    parentNodeId: null,
    ...partial,
  };
}

export function createGroupField(
  partial: PartialGroupField,
  parentNodeId: UUID,
): GroupField {
  return {
    title_en: '',
    title_nl: '',
    children: [],
    modifiedFields: [],
    protoChain: [],
    parentNodeId,
    ...partial,
  };
}

export function createCheckpointField(
  partial: PartialCheckpointField,
  parentNodeId: UUID,
): CheckpointField {
  return {
    title_en: '',
    title_nl: '',
    description_en: '',
    description_nl: '',
    value: null,
    modifiedFields: [],
    protoChain: [],
    parentNodeId,
    attachments: [],
    ...partial,
  };
}

export function createNumericField(
  partial: PartialNumericField,
  parentNodeId: UUID,
): NumericField {
  return {
    title_en: '',
    title_nl: '',
    description_en: '',
    description_nl: '',
    value: null,
    modifiedFields: [],
    protoChain: [],
    parentNodeId,
    attachments: [],
    ...partial,
  };
}

export function createTaskField(
  partial: PartialTaskField,
  parentNodeId: UUID,
): TaskField {
  return {
    title_en: '',
    title_nl: '',
    description_en: '',
    description_nl: '',
    value: null,
    modifiedFields: [],
    protoChain: [],
    parentNodeId,
    attachments: [],
    ...partial,
  };
}

// ---------------------------------------------------------------------------
// completePartialField - takes a partial field and produces a full field
// ---------------------------------------------------------------------------

export function completePartialField(
  partialField: PartialField,
  parentNodeId: UUID,
): Field {
  switch (partialField.type) {
    case 'GROUP_FIELD':
      return createGroupField(partialField, parentNodeId);
    case 'CHECKPOINT_FIELD':
      return createCheckpointField(partialField, parentNodeId);
    case 'NUMERIC_FIELD':
      return createNumericField(partialField, parentNodeId);
    case 'TASK_FIELD':
      return createTaskField(partialField, parentNodeId);
    default: {
      const _exhaustive: never = partialField;
      throw new Error(
        `Unexpected partial field type: ${(_exhaustive as PartialField).type}`,
      );
    }
  }
}
